import React, { ReactNode, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { useSiteRegion } from "utils/hooks/useSiteSection";
import { useIsMobile } from "utils/hooks/useIsMobile";

type AnalyticsProviderProps = {
	children: ReactNode;
};

declare global {
	interface Window {
		clarity?: (event: string, payload: any) => void;
		dataLayer?: Record<string, any>[];
		fcWidget?: any;
	}
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
	const { user } = useUser();
	const region = useSiteRegion();
	const isMobile = useIsMobile();

	const toggleFreshchatVisibility = (isLoggedIn: boolean, forceVisible: boolean = false) => {
		const fcFrame = document.getElementById("fc_frame");
		if (fcFrame) {
			fcFrame.classList.remove("freshchat-hidden", "freshchat-visible");
			if (forceVisible) {
				fcFrame.classList.add("freshchat-visible");
			} else if (isLoggedIn && isMobile) {
				fcFrame.classList.add("freshchat-hidden");
			} else {
				fcFrame.classList.add("freshchat-visible");
			}
		}
	};

	// Function to set up Freshchat event listeners (only for mobile logged-in users)
	const setupFreshchatListeners = () => {
		if (typeof window !== "undefined" && window.fcWidget && isMobile && user?.id) {
			window.fcWidget.on("widget:opened", () => {
				toggleFreshchatVisibility(!!user?.id, true); // Force visible when opened
			});

			window.fcWidget.on("widget:closed", () => {
				toggleFreshchatVisibility(!!user?.id, false); // Back to normal state when closed
			});
		}
	};

	useEffect(() => {
		if (!user?.id || !user?.emailAddresses || user.emailAddresses.length === 0) return;

		const userId = user.id;
		const email = user.emailAddresses[0].emailAddress;

		// Microsoft Clarity
		if (typeof window !== "undefined" && typeof window.clarity === "function") {
			window.clarity("identify", email);
		}

		if (typeof window !== "undefined") {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				userId: userId,
			});
		}

		return () => {
			if (typeof window !== "undefined" && window.dataLayer) {
				if (window.dataLayer.length > 100) {
					window.dataLayer = window.dataLayer.slice(-100);
				}
			}
		};
	}, [user]);

	// Toggle Freshchat visibility based on authentication state
	useEffect(() => {
		if (typeof window !== "undefined") {
			// Try to toggle immediately if fc_frame exists
			toggleFreshchatVisibility(!!user?.id);

			// Also set up a periodic check for when fc_frame loads
			const checkInterval = setInterval(() => {
				const fcFrame = document.getElementById("fc_frame");
				if (fcFrame) {
					toggleFreshchatVisibility(!!user?.id);
					setupFreshchatListeners();
					clearInterval(checkInterval);
				}
			}, 100);

			// Clear interval after 10 seconds to avoid infinite checking
			setTimeout(() => clearInterval(checkInterval), 10000);

			return () => clearInterval(checkInterval);
		}
	}, [user?.id, isMobile]);

	useEffect(() => {
		if (region !== "au") return;
		initFreshchat(user, isMobile, toggleFreshchatVisibility, setupFreshchatListeners);

		// Cleanup function for Freshchat
		return () => {
			if (typeof window !== "undefined" && window.fcWidget) {
				try {
					window.fcWidget.destroy();
					// Clear the widget reference
					delete window.fcWidget;
				} catch (e) {
					// Ignore errors if widget is already destroyed
				}
			}
		};
	}, [user, region]);

	return <>{children}</>;
};

function initFreshchat(user?: ReturnType<typeof useUser>["user"], isMobile?: boolean, toggleFreshchatVisibility?: (isLoggedIn: boolean, forceVisible?: boolean) => void, setupFreshchatListeners?: () => void) {
	const userId = user?.id;
	const email = user?.emailAddresses?.[0]?.emailAddress || "";
	const firstName = user?.firstName || "";
	const initializeFreshchat = () => {
		if (!window.fcWidget) return;

		window.fcWidget.init({
			token: "c46b4947-1b87-48be-9e25-9ba9c505d35d",
			host: "https://abcopro.freshchat.com",
		});

		if (userId && email) {
			window.fcWidget.setExternalId(userId);
			window.fcWidget.user.setFirstName(firstName);
			window.fcWidget.user.setEmail(email);
			window.fcWidget.user.setProperties({
				plan: "Abconet",
				status: "Active",
			});
		}

		// Apply visibility class and set up listeners after initialization
		setTimeout(() => {
			if (toggleFreshchatVisibility) {
				toggleFreshchatVisibility(!!userId);
			}
			// Set up event listeners for open/close events (only on mobile)
			if (setupFreshchatListeners) {
				setupFreshchatListeners();
			}
		}, 500);
	};

	if (typeof window === "undefined") return;

	if (window.fcWidget) {
		initializeFreshchat();
	} else if (!document.getElementById("Freshchat-js-sdk")) {
		const script = document.createElement("script");
		script.id = "Freshchat-js-sdk";
		script.async = true;
		script.src = "https://abcopro.freshchat.com/js/widget.js";
		script.onload = initializeFreshchat;
		document.head.appendChild(script);
	}
}
